import type { ScaleLinear, ScaleTime } from 'd3-scale'
import { curveStepAfter, line } from 'd3-shape'
import { useMemo } from 'react'

import type { AreaGraphElement, OverlayLine } from '@/features/bidding/components/cascading/charts/types'
import { getNextDatetime } from '@/features/bidding/components/cascading/charts/utils/getNextDatetime'

type OverlayLinesProps = {
  data: AreaGraphElement[]
  lines: OverlayLine[]
  timeScale: ScaleTime<number, number>
  valueScale: ScaleLinear<number, number>
}

/**
 * Renders overlay lines on top of area graph layers.
 * Uses curveStepAfter to match the area graph's stepped appearance.
 */
function OverlayLines({ data, lines, timeScale, valueScale }: Readonly<OverlayLinesProps>) {
  const datetimes = useMemo(() => data.map((d) => d.datetime), [data])

  const linePaths = useMemo(() => {
    if (data.length === 0) return []

    // Duplicate datapoints to extend to next datetime (matching AreaGraphLayers behavior)
    const expandedData = data.flatMap((d) => [d, { ...d, datetime: getNextDatetime(datetimes, d.datetime) }])

    return lines.map((lineConfig) => {
      const lineGenerator = line<AreaGraphElement>()
        .x((d) => timeScale(d.datetime))
        .y((d) => valueScale(lineConfig.getValue(d)))
        .curve(curveStepAfter)
        .defined((d) => Number.isFinite(lineConfig.getValue(d)))

      return {
        key: lineConfig.key,
        path: lineGenerator(expandedData) ?? '',
        color: lineConfig.color,
        strokeWidth: lineConfig.isHighlighted ? (lineConfig.strokeWidth ?? 1) * 2 : (lineConfig.strokeWidth ?? 1),
        opacity: lineConfig.isHighlighted ? 1 : (lineConfig.opacity ?? 0.5),
        isHighlighted: lineConfig.isHighlighted,
        strokeDasharray: lineConfig.strokeDasharray,
      }
    })
  }, [data, datetimes, lines, timeScale, valueScale])

  // Sort so highlighted lines render last (on top)
  const sortedPaths = useMemo(() => {
    const nonHighlighted = linePaths.filter((p) => !p.isHighlighted)
    const highlighted = linePaths.filter((p) => p.isHighlighted)
    return [...nonHighlighted, ...highlighted]
  }, [linePaths])

  return (
    <g className="overlay-lines">
      {sortedPaths.map((linePath) => (
        <path
          key={linePath.key}
          d={linePath.path}
          fill="none"
          opacity={linePath.opacity}
          stroke={linePath.color}
          strokeDasharray={linePath.strokeDasharray}
          strokeWidth={linePath.strokeWidth}
        />
      ))}
    </g>
  )
}

export default OverlayLines
